import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";
import { getSeedFromMemory } from "./SeedPrefetchService";

type FetchCheckCacheEntry = {
    expires: number;
    data: any;
};

type CheckResult = {
    compatibility?: string;
    custom_compatibility?: string;
    compatibility_custom?: string;
    availability?: Record<string, Record<string, boolean>>;
    [key: string]: unknown;
};

/**
 * Build the check seed key used for offline fallback.
 * Stable across colors / trims / cabs since the production /api/v1/check
 * response is the same shape regardless of which color is requested
 * (it always returns the full per-seat-per-color matrix).
 */
const buildCheckSeedKey = (
    year: string | number,
    make: string,
    model: string,
    wtype: string,
): string => `check_${year}_${make}_${model}_${wtype}`;

/**
 * Synthesize a check response from a seeded `vehicle_availability` entry.
 *
 * `vehicle_availability` is the configurator data (used by biz2 + vehicle_configurator).
 * Its `data.configurator[].sku_mappings[].availability` matrices are merged
 * into a single seat→color availability map — that's the same shape `check`
 * normally returns under `data.availability`, so we can stand in for it
 * when /api/v1/check is unreachable.
 */
const deriveCheckFromVehicleAvailability = (
    year: string | number,
    make: string,
    model: string,
): CheckResult | null => {
    const vaKey = `vehicle_availability_${year}_${make}_${model}`;
    const va = getSeedFromMemory<any>(IDB_STORES.VEHICLE_AVAILABILITY, vaKey);
    if (!va) return null;
    // Seed stores the inner data directly (no `data` wrapper);
    // live API shape has { data: { vehicle, availability, configurator } }.
    const vaData = va.data ?? va;
    if (!vaData) return null;

    const compatibility = vaData.vehicle?.compatibility ?? "Y";
    const merged: Record<string, Record<string, boolean>> = {};
    const configurator = Array.isArray(vaData.configurator) ? vaData.configurator : [];
    for (const cfg of configurator) {
        const mappings = Array.isArray(cfg?.sku_mappings) ? cfg.sku_mappings : [];
        for (const mapping of mappings) {
            const avail = mapping?.availability || {};
            for (const [seat, byColor] of Object.entries(avail)) {
                if (!byColor || typeof byColor !== "object") continue;
                if (!merged[seat]) merged[seat] = {};
                for (const [color, val] of Object.entries(byColor as Record<string, unknown>)) {
                    if (typeof val === "boolean") {
                        merged[seat][color] = merged[seat][color] || val;
                    }
                }
            }
        }
    }

    // Fall back to the top-level `availability` field on the VA response when
    // the configurator block doesn't supply it (vehicles without sku_mappings).
    if (Object.keys(merged).length === 0 && vaData.availability && typeof vaData.availability === "object") {
        for (const [seat, byColor] of Object.entries(vaData.availability)) {
            if (!byColor || typeof byColor !== "object") continue;
            merged[seat] = {};
            for (const [color, val] of Object.entries(byColor as Record<string, unknown>)) {
                if (typeof val === "boolean") merged[seat][color] = val;
            }
        }
    }

    return {
        compatibility,
        custom_compatibility: "N",
        compatibility_custom: "N",
        availability: merged,
    };
};

const FETCH_CHECK_CACHE_TTL = 60 * 1000; // 60 seconds
const fetchCheckCache = new Map<string, FetchCheckCacheEntry>();

const buildFetchCheckCacheKey = (params: Record<string, any>) => {
    return JSON.stringify(
        Object.keys(params)
            .sort()
            .reduce((acc, key) => {
                acc[key] = params[key];
                return acc;
            }, {} as Record<string, any>)
    );
};

const fetchCheck = async (
    year,
    make,
    model,
    trim,
    color,
    seat,
    cabSize = "",
    step = ""
) => {
    if (!year) return [];
    if (!make) return [];
    if (!model) return [];

    let wtype = "normal";
    if (window?.is_real_truck) {
        wtype = "realtruck";
    } else if (window?.scs_force_custom) {
        wtype = "custom";
    } else if (window?.scs_custom_endpoint) {
        wtype = "realtruck";
    }

    const params = {
        year: year,
        make: make,
        model: model,
        colorCode: color || "Black",
        wtype: wtype,
    };
    if (trim && trim !== "") {
        params["trim"] = trim;
    }
    if (cabSize && cabSize !== "") {
        params["cab"] = cabSize;
    }

    if (seat && !window.scs_new_design_2026) {
        params["type"] = seat;
    }
    // if (step === "change_seat") {
    //     params["type"] = seat;
    // }
    if (window.scs_page_seo === true) {
        params["page_seo"] = "1";
    }

    const cacheKey = buildFetchCheckCacheKey(params);
    const cached = fetchCheckCache.get(cacheKey);

    if (cached && cached.expires > Date.now()) {
        return cached.data;
    }

    // Inner function: network request + response processing.
    // This is what gets stored in (and served from) IDB on network failure.
    const fetchAndProcess = async () => {
        const urlMakes = "https://scsbiz3.wearevisum.com/api/v1/check";
        const response = await fetch(urlMakes, {
            method: "POST",
            mode: "cors",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch check: ${response.statusText}`);
        }

        const resp = await response.json();
        resp.data.compatibility_custom = resp.data.custom_compatibility;

        // Hard override: for configurator flow, force HF for 2002-2008 RAM 1500/2500/3500/4500/5500
        const isConfiguratorOverride = window?.scs_vehicle_configurator === true &&
            typeof year !== 'undefined' &&
            typeof make !== 'undefined' &&
            typeof model !== 'undefined' &&
            Number(year) >= 2002 && Number(year) <= 2008 &&
            String(make).toLowerCase() === 'ram' &&
            /(^|\D)(1500|2500|3500|4500|5500)(\D|$)/i.test(String(model));

        if (isConfiguratorOverride) {
            resp.data.compatibility = 'HF';
            // also set custom compatibility to match if present
            resp.data.custom_compatibility = resp.data.custom_compatibility || 'HF';
        }

        // console.log('check resp', resp)

        // Preserve HF when configurator override applied; otherwise normalize HF -> F as before
        if (!isConfiguratorOverride && resp.data.compatibility === "HF") {
            resp.data.compatibility = "F";
        }
        if (
            resp.data.compatibility === "N" ||
            resp.data.compatibility === "DNE" ||
            resp.data.compatibility === "NAON"
        ) {
            resp.data.compatibility = "N";
        }

        if (window.scs_force_normal === true) {
            resp.data.custom_compatibility = "N";
            resp.data.compatibility_custom = "N";
        } else {
            if (window.scs_force_custom === true) {
                resp.data.compatibility = "N";
            }
        }

        resp.data.custom_compatibility = resp.data.compatibility_custom;

        // if (window.scs_new_design_2026) {
        //     const keyOrder = ["Bundle", "Front & Back Bundle", "Front Seats", "Back Bench", "Universal Back Bench"];
        //     // need to sort resp.data.availability by key order from keyOrder
        //
        //     const availability = resp.data.availability;
        //
        //     resp.data.availability = {
        //         ...Object.fromEntries(
        //             keyOrder
        //                 .filter(key => key in availability)
        //                 .map(key => [key, availability[key]])
        //         ),
        //         ...Object.fromEntries(
        //             Object.entries(availability).filter(
        //                 ([key]) => !keyOrder.includes(key)
        //             )
        //         ),
        //     };
        //
        //
        //     console.log('check resp 2:', resp)
        // }

        return resp.data;
    };

    // Seed fallbacks: dedicated pre-fetched check entry first, then a
    // derivation from the bundled vehicle_availability data.
    const seedFallback = (): CheckResult | null => {
        const seedKey = buildCheckSeedKey(year, make, model, wtype);
        const seeded = getSeedFromMemory<CheckResult>(IDB_STORES.CHECK, seedKey);
        if (seeded && typeof seeded === "object") {
            console.log("[CheckService] using bundled check seed:", seedKey);
            return seeded;
        }
        const derived = deriveCheckFromVehicleAvailability(year, make, model);
        if (derived) {
            console.log("[CheckService] derived check from vehicle_availability seed:", year, make, model);
            return derived;
        }
        return null;
    };

    let result: CheckResult | undefined;
    try {
        result = await withIDBFallback(
            IDB_STORES.CHECK,
            cacheKey,
            IDB_TTL.CHECK,
            fetchAndProcess
        ) as CheckResult;
    } catch (err) {
        console.warn("[CheckService] network+IDB failed, attempting seed fallback:", err);
        const fallback = seedFallback();
        if (fallback) {
            result = fallback;
        } else {
            throw err;
        }
    }

    // withIDBFallback may resolve with a malformed/empty value if the cached
    // entry was written by a prior failed run — guard with the same fallback.
    if (!result || typeof result !== "object" || !result.compatibility) {
        const fallback = seedFallback();
        if (fallback) result = fallback;
    }

    if (result && !(result as any).error) {
        fetchCheckCache.set(cacheKey, {
            expires: Date.now() + FETCH_CHECK_CACHE_TTL,
            data: result,
        });
    }

    return result;
};

export default fetchCheck;
